package com.shadowings.gero.presentation.util

import androidx.compose.foundation.layout.BoxWithConstraintsScope
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import com.shadowings.gero.getPlatform
import com.shadowings.gero.util.FeedbackType
import com.shadowings.gero.util.currentDateAsString
import com.shadowings.gero.util.currentWindowSizePx
import com.shadowings.gero.util.getAppVersionName
import com.shadowings.gero.util.getDeviceModel
import com.shadowings.gero.util.systemLanguageCode

enum class DeviceClass {
    PHONE_SMALL,
    PHONE_MEDIUM,
    PHONE_LARGE,
    TABLET_MEDIUM,
    TABLET_LARGE,
    WEB_FULL;

    val isPhone: Boolean
        get() = this == PHONE_SMALL || this == PHONE_MEDIUM || this == PHONE_LARGE

    val isTablet: Boolean
        get() = this == TABLET_MEDIUM || this == TABLET_LARGE
}

enum class DeviceOrientation {
    PORTRAIT,
    LANDSCAPE
}

data class DeviceInfo(
    val deviceClass: DeviceClass,
    val orientation: DeviceOrientation
) {
    val isPortrait: Boolean
        get() = orientation == DeviceOrientation.PORTRAIT

    val isLandscape: Boolean
        get() = orientation == DeviceOrientation.LANDSCAPE

    val isPhone: Boolean
        get() = deviceClass.isPhone

    val isTablet: Boolean
        get() = deviceClass.isTablet
}

/**
 * Returns the DeviceClass based on the dimensions in Dp.
 * Device class is determined by the smallest screen width (shortest dimension).
 */
fun getDeviceClass(width: Dp, height: Dp): DeviceClass {
    // Wide web viewports get their own top tier, keyed on actual width (not the smallest
    // dimension below) since a browser window is normally much wider than it is tall.
    val smallestWidth = minOf(width, height)
    return when {
        getPlatform().isWeb && width >= 1200.dp -> DeviceClass.WEB_FULL
        smallestWidth <= 360.dp -> DeviceClass.PHONE_SMALL
        smallestWidth < 400.dp -> DeviceClass.PHONE_MEDIUM
        smallestWidth < 600.dp -> DeviceClass.PHONE_LARGE
        smallestWidth < 800.dp -> DeviceClass.TABLET_MEDIUM
        smallestWidth < 1200.dp -> DeviceClass.TABLET_LARGE
        else -> DeviceClass.WEB_FULL
    }
}

/**
 * Returns the DeviceOrientation based on the dimensions in Dp.
 */
fun getDeviceOrientation(width: Dp, height: Dp): DeviceOrientation {
    return if (width > height) DeviceOrientation.LANDSCAPE else DeviceOrientation.PORTRAIT
}

/**
 * Returns the DeviceInfo based on the dimensions in Dp.
 */
fun getDeviceInfo(width: Dp, height: Dp): DeviceInfo {
    return DeviceInfo(
        deviceClass = getDeviceClass(width, height),
        orientation = getDeviceOrientation(width, height)
    )
}

/**
 * Helper properties for BoxWithConstraintsScope to easily get device classification details.
 */
val BoxWithConstraintsScope.deviceClass: DeviceClass
    get() = getDeviceClass(maxWidth, maxHeight)

val BoxWithConstraintsScope.deviceOrientation: DeviceOrientation
    get() = getDeviceOrientation(maxWidth, maxHeight)

val BoxWithConstraintsScope.deviceInfo: DeviceInfo
    get() = getDeviceInfo(maxWidth, maxHeight)

/**
 * Composable that reactively resolves and remembers the current window's DeviceInfo.
 */
@Composable
fun rememberDeviceInfo(): DeviceInfo {
    val density = LocalDensity.current
    val containerSize = currentWindowSizePx()
    val widthDp = with(density) { containerSize.width.toDp() }
    val heightDp = with(density) { containerSize.height.toDp() }
    return remember(widthDp, heightDp) {
        getDeviceInfo(widthDp, heightDp)
    }
}

/**
 * Composable that reactively resolves and remembers the current window's DeviceClass.
 */
@Composable
fun rememberDeviceClass(): DeviceClass = rememberDeviceInfo().deviceClass

/**
 * Composable that reactively resolves and remembers the current window's DeviceOrientation.
 */
@Composable
fun rememberDeviceOrientation(): DeviceOrientation = rememberDeviceInfo().orientation

private val FeedbackType.label: String
    get() = when (this) {
        FeedbackType.GENERAL -> "General Feedback"
        FeedbackType.UPDATE_ISSUE -> "Post-Update Issue"
        FeedbackType.QUIZ_ERROR -> "Quiz Error"
        FeedbackType.MORE_LESSONS -> "Request More Lessons"
    }

/**
 * Multi-line device/app/screen diagnostic block meant to travel alongside feedback emails (as an
 * attachment on platforms that support it, appended to the body otherwise), so reports come with
 * the debug info users would otherwise have to type out by hand. [extra] appends feedback-specific
 * details (e.g. quiz question/answer, lesson id, user stats).
 */
@Composable
fun rememberFeedbackDebugInfo(type: FeedbackType, extra: String = ""): String {
    val density = LocalDensity.current
    val containerSize = currentWindowSizePx()
    val widthDp = with(density) { containerSize.width.toDp() }
    val heightDp = with(density) { containerSize.height.toDp() }
    return remember(widthDp, heightDp, type, extra) {
        val base = "Type: ${type.label}\n" +
            "Date: ${currentDateAsString()}\n" +
            "App Version: ${getAppVersionName()}\n" +
            "Device: ${getDeviceModel()}\n" +
            "OS: ${getPlatform().name}\n" +
            "Language: ${systemLanguageCode()}\n" +
            "Screen: ${containerSize.width}x${containerSize.height}px " +
            "(${widthDp.value.toInt()}x${heightDp.value.toInt()}dp), " +
            "density=${density.density}x, ~${(density.density * 160).toInt()}dpi"
        if (extra.isBlank()) base else "$base\n\n$extra"
    }
}
